import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaMoneyBillWave, 
  FaShoppingCart, 
  FaBoxOpen, 
  FaCheckCircle,
  FaTruck,
  FaTimesCircle,
  FaSpinner,
  FaCalendar,
} from 'react-icons/fa';
import { ChevronDown, ChevronUp, Loader } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

import './retailers.css';

export default function RetailerOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalSales: 0,
    ordersCount: 0,
    inventoryCount: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      
      // Fetch orders and products in parallel
      const [ordersResponse, productsResponse] = await Promise.all([
        api.getRetailerOrders(),
        api.getRetailerProducts()
      ]);

      if (ordersResponse.success) {
        const orders = ordersResponse.data;
        
        // Calculate total sales from orders
        const totalSales = orders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);
        
        // Set stats
        setStats({
          totalSales,
          ordersCount: orders.length,
          inventoryCount: productsResponse.success ? productsResponse.data.length : 0
        });

        // Set recent orders (last 3)
        const sortedOrders = orders
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 3);
        setRecentOrders(sortedOrders);
      }
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "order-status-rtl completed-rtl";
      case "processing":
        return "order-status-rtl processing-rtl";
      case "shipped":
        return "order-status-rtl shipped-rtl";
      case "cancelled":
        return "order-status-rtl cancelled-rtl";
      case "pending":
        return "order-status-rtl processing-rtl";
      default:
        return "order-status-rtl";
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return <FaCheckCircle size={14} />;
      case "processing":
      case "pending":
        return <FaSpinner size={14} className="animate-spin" />;
      case "shipped":
        return <FaTruck size={14} />;
      case "cancelled":
        return <FaTimesCircle size={14} />;
      default:
        return null;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(parseFloat(amount));
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get user's display name
  const getUserDisplayName = () => {
    if (user?.name) {
      return user.name;
    } else if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'Retailer';
  };

  // Statistics cards data
  const statsCards = [
    { 
      id: 1, 
      title: 'Total Sales', 
      value: formatCurrency(stats.totalSales), 
      icon: <FaMoneyBillWave className="stat-icon-rtl" />,
      positive: true
    },
    { 
      id: 2, 
      title: 'Orders', 
      value: stats.ordersCount, 
      icon: <FaShoppingCart className="stat-icon-rtl" />,
      positive: true
    },
    { 
      id: 3, 
      title: 'Inventory Items', 
      value: stats.inventoryCount, 
      icon: <FaBoxOpen className="stat-icon-rtl" />,
      positive: false
    },
  ];

  if (loading) {
    return (
      <div className="retailer-dashboard-container-rtl">
        <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
          <Loader className="animate-spin" size={32} />
          <p>Loading overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="retailer-dashboard-container-rtl">
      {/* Welcome section */}
      <div className="welcome-section-rtl">
        <div className="welcome-text-rtl">
          <h1>Welcome back, {getUserDisplayName()}!</h1>
          <p>Here's an overview of your retail business</p>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-overview-rtl">
        <div className="section-header-rtl">
          <h2>Performance Overview</h2>
        </div>
        <div className="stats-cards-rtl">
          {statsCards.map(stat => (
            <div className="stat-card-rtl" key={stat.id}>
              <div className="stat-icon-wrapper-rtl">
                {stat.icon}
              </div>
              <div className="stat-info-rtl">
                <h3>{stat.title}</h3>
                <div className="stat-details-rtl">
                  <span className="stat-value-rtl">{stat.value}</span>
                  <span className={`stat-change-rtl ${stat.positive ? 'positive-rtl' : 'negative-rtl'}`}>
                    {stat.change}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Orders Section */}
      <div className="recent-orders-rtl">
        <div className="section-header-rtl">
          <h2>Recent Orders</h2>
          <Link to="/retailer-dashboard/orders" className="view-all-link-rtl">
            View All
          </Link>
        </div>

        <div className="orders-list-rtl">
          {recentOrders.length > 0 ? (
            recentOrders.map((order) => (
              <div key={order.order_id} className="order-card-rtl">
                <div
                  className="order-header-rtl"
                  onClick={() => toggleOrderExpansion(order.order_id)}
                >
                  <div className="order-main-info-rtl">
                    <div className="order-id-wrapper-rtl">
                      <span className="order-id-label-rtl">Order ID:</span>
                      <span className="order-id-rtl">#{order.order_id}</span>
                    </div>
                    <div className="order-farmer-rtl">
                      Reference: {order.payment_reference}
                    </div>
                  </div>

                  <div className="order-secondary-info-rtl">
                    <div className={getStatusClass(order.status)}>
                      {getStatusIcon(order.status)}
                      <span>{order.status.charAt(0).toUpperCase() + order.status.slice(1)}</span>
                    </div>

                    <div className="order-date-rtl">
                      <FaCalendar size={14} />
                      <span>{formatDate(order.created_at)}</span>
                    </div>

                    <div className="order-total-rtl">
                      {formatCurrency(order.total_amount)}
                    </div>
                    <button
                      className="expand-button-rtl"
                      aria-label="Expand order details"
                    >
                      {expandedOrders[order.order_id] ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </button>
                  </div>
                </div>

                {expandedOrders[order.order_id] && (
                  <div className="order-details-rtl">
                    <div className="order-info-section">
                      <div className="order-detail-item">
                        <strong>Shipping Address:</strong>
                        <span>{order.shipping_address}</span>
                      </div>
                      <div className="order-detail-item">
                        <strong>Phone:</strong>
                        <span>{order.phone}</span>
                      </div>
                    </div>

                    <h4 className="order-items-title-rtl">Order Items</h4>
                    <table className="order-items-table-rtl">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Farmer</th>
                          <th>Quantity</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.product_name || 'Product'}</td>
                            <td>{item.farmer?.name || 'Unknown'}</td>
                            <td>{item.quantity}</td>
                            <td>{formatCurrency(item.price)}</td>
                            <td>{formatCurrency(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="4" className="order-total-label-rtl">
                            Total
                          </td>
                          <td className="order-grand-total-rtl">
                            {formatCurrency(order.total_amount)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="no-orders-rtl">
              <p>No recent orders found.</p>
              <p>
                <Link to="/retailer-dashboard/products" className="start-shopping-link">
                  Start shopping to see your orders here!
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}